const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:4000/api").replace(/\/$/, "");

const TOKEN_KEY = "srishti_admin_token";
const EXPIRES_KEY = "srishti_admin_expires_at";

export function getAdminToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAdminSession({ token, expiresAt }) {
  localStorage.setItem(TOKEN_KEY, token);
  if (expiresAt) localStorage.setItem(EXPIRES_KEY, expiresAt);
}

export function clearAdminSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EXPIRES_KEY);
}

export function isAdminSessionValid() {
  const token = getAdminToken();
  const exp = localStorage.getItem(EXPIRES_KEY);
  if (!token) return false;
  if (!exp) return true;
  return new Date(exp).getTime() > Date.now();
}

export async function apiFetch(path, options = {}) {
  const headers = { ...(options.headers || {}) };
  const token = getAdminToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const isFormData = options.body instanceof FormData;
  if (!isFormData && options.body && typeof options.body === "object" && !(options.body instanceof Blob)) {
    headers["Content-Type"] = "application/json";
    options.body = JSON.stringify(options.body);
  }

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  let data = {};
  try {
    data = await res.json();
  } catch {
    data = {};
  }

  if (!res.ok) {
    const err = new Error(data.error || `Request failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

export async function loginAdmin(email, password) {
  return apiFetch("/auth/login", {
    method: "POST",
    body: { email, password },
  });
}

export async function logoutAdmin() {
  try {
    await apiFetch("/auth/logout", { method: "POST" });
  } finally {
    clearAdminSession();
  }
}

export async function fetchBanners(isActive) {
  const q = isActive === undefined ? "" : `?isActive=${isActive}`;
  return apiFetch(`/admin/banners${q}`);
}

export async function createBanner({ title, file }) {
  const fd = new FormData();
  fd.append("title", title);
  fd.append("media", file);
  return apiFetch("/admin/banners", { method: "POST", body: fd });
}

export async function updateBanner(id, { title, file, active }) {
  const fd = new FormData();
  if (title !== undefined) fd.append("title", title);
  if (active !== undefined) fd.append("active", String(active));
  if (file) fd.append("media", file);
  return apiFetch(`/admin/banners/${id}`, { method: "PATCH", body: fd });
}

export async function deleteBanner(id) {
  return apiFetch(`/admin/banners/${id}`, { method: "DELETE" });
}

/** Public site */
export async function fetchPublicBanners(isActive = true) {
  return apiFetch(`/banners?isActive=${isActive}`);
}

export async function fetchPublicAchievementCategories(isActive = true) {
  return apiFetch(`/achievement-categories?isActive=${isActive}`);
}

// ─── Achievement categories ───────────────────────────────────────────────────

export async function fetchAchievementCategories(isActive) {
  const q = isActive === undefined ? "" : `?isActive=${isActive}`;
  return apiFetch(`/admin/achievement-categories${q}`);
}

export async function createAchievementCategory({ name, description, file }) {
  const fd = new FormData();
  fd.append("name", name);
  fd.append("description", description ?? "");
  if (file) fd.append("media", file);
  return apiFetch("/admin/achievement-categories", { method: "POST", body: fd });
}

export async function updateAchievementCategory(id, { name, description, file, active, slug }) {
  const fd = new FormData();
  if (name !== undefined) fd.append("name", name);
  if (slug !== undefined) fd.append("slug", slug);
  if (description !== undefined) fd.append("description", description);
  if (active !== undefined) fd.append("active", String(active));
  if (file) fd.append("media", file);
  return apiFetch(`/admin/achievement-categories/${id}`, { method: "PATCH", body: fd });
}

export async function deleteAchievementCategory(id) {
  return apiFetch(`/admin/achievement-categories/${id}`, { method: "DELETE" });
}

// ─── School achievements ────────────────────────────────────────────────────

export async function fetchSchoolAchievements({ isActive, categoryId } = {}) {
  const params = new URLSearchParams();
  if (isActive !== undefined) params.set("isActive", String(isActive));
  if (categoryId !== undefined) params.set("categoryId", String(categoryId));
  const q = params.toString() ? `?${params}` : "";
  return apiFetch(`/admin/school-achievements${q}`);
}

export async function createSchoolAchievement({ name, categoryId, file }) {
  const fd = new FormData();
  fd.append("name", name);
  fd.append("categoryId", String(categoryId));
  fd.append("media", file);
  return apiFetch("/admin/school-achievements", { method: "POST", body: fd });
}

export async function createSchoolAchievementsBulk({ categoryId, files, namePrefix }) {
  const fd = new FormData();
  fd.append("categoryId", String(categoryId));
  if (namePrefix) fd.append("namePrefix", namePrefix);
  files.forEach((file) => fd.append("media", file));
  return apiFetch("/admin/school-achievements/bulk", { method: "POST", body: fd });
}

export async function updateSchoolAchievement(id, { name, categoryId, file, active }) {
  const fd = new FormData();
  if (name !== undefined) fd.append("name", name);
  if (categoryId !== undefined) fd.append("categoryId", String(categoryId));
  if (active !== undefined) fd.append("active", String(active));
  if (file) fd.append("media", file);
  return apiFetch(`/admin/school-achievements/${id}`, { method: "PATCH", body: fd });
}

export async function deleteSchoolAchievement(id) {
  return apiFetch(`/admin/school-achievements/${id}`, { method: "DELETE" });
}

// ─── Faculty ────────────────────────────────────────────────────────────────

export async function fetchFaculty(isActive) {
  const q = isActive === undefined ? "" : `?isActive=${isActive}`;
  return apiFetch(`/admin/faculty${q}`);
}

export async function createFaculty(body) {
  return apiFetch("/admin/faculty", { method: "POST", body });
}

export async function updateFaculty(id, body) {
  return apiFetch(`/admin/faculty/${id}`, { method: "PATCH", body });
}

export async function deleteFaculty(id) {
  return apiFetch(`/admin/faculty/${id}`, { method: "DELETE" });
}

/** Public site */
export async function fetchPublicFaculty(isActive = true) {
  const q = isActive === undefined ? "" : `?isActive=${isActive}`;
  return apiFetch(`/faculty${q}`);
}

export async function fetchPublicGalleryCategories(isActive = true) {
  const q = isActive === undefined ? "" : `?isActive=${isActive}`;
  return apiFetch(`/gallery-categories${q}`);
}

export async function fetchPublicGalleryPhotos({ isActive = true, categoryId, page = 1, limit = 10 } = {}) {
  const params = new URLSearchParams();
  if (isActive !== undefined) params.set("isActive", isActive);
  if (categoryId != null)     params.set("categoryId", categoryId);
  params.set("page", page);
  params.set("limit", limit);
  return apiFetch(`/gallery-photos?${params.toString()}`);
}

export async function fetchPublicInfraCategories(isActive = true) {
  const q = isActive === undefined ? "" : `?isActive=${isActive}`;
  return apiFetch(`/infrastructure-categories${q}`);
}

export async function fetchPublicInfraItems({ isActive = true, categoryId } = {}) {
  const params = new URLSearchParams();
  if (isActive !== undefined) params.set("isActive", isActive);
  if (categoryId != null)     params.set("categoryId", categoryId);
  return apiFetch(`/infrastructure-items?${params.toString()}`);
}

// ─── Gallery categories ───────────────────────────────────────────────────────

export async function fetchGalleryCategories(isActive) {
  const q = isActive === undefined ? "" : `?isActive=${isActive}`;
  return apiFetch(`/admin/gallery-categories${q}`);
}

export async function createGalleryCategory(body) {
  return apiFetch("/admin/gallery-categories", { method: "POST", body });
}

export async function updateGalleryCategory(id, body) {
  return apiFetch(`/admin/gallery-categories/${id}`, { method: "PATCH", body });
}

export async function deleteGalleryCategory(id) {
  return apiFetch(`/admin/gallery-categories/${id}`, { method: "DELETE" });
}

// ─── Gallery photos ───────────────────────────────────────────────────────────

export async function fetchGalleryPhotos({ isActive, categoryId } = {}) {
  const params = new URLSearchParams();
  if (isActive !== undefined) params.set("isActive", String(isActive));
  if (categoryId !== undefined) params.set("categoryId", String(categoryId));
  const q = params.toString() ? `?${params}` : "";
  return apiFetch(`/admin/gallery-photos${q}`);
}

export async function createGalleryPhoto({ title, caption, categoryId, file }) {
  const fd = new FormData();
  fd.append("title", title);
  if (caption) fd.append("caption", caption);
  fd.append("categoryId", String(categoryId));
  fd.append("media", file);
  return apiFetch("/admin/gallery-photos", { method: "POST", body: fd });
}

export async function createGalleryPhotosBulk({ categoryId, files, titlePrefix, caption }) {
  const fd = new FormData();
  fd.append("categoryId", String(categoryId));
  if (titlePrefix) fd.append("titlePrefix", titlePrefix);
  if (caption) fd.append("caption", caption);
  files.forEach((file) => fd.append("media", file));
  return apiFetch("/admin/gallery-photos/bulk", { method: "POST", body: fd });
}

export async function updateGalleryPhoto(id, { title, caption, categoryId, file, active }) {
  const fd = new FormData();
  if (title !== undefined) fd.append("title", title);
  if (caption !== undefined) fd.append("caption", caption);
  if (categoryId !== undefined) fd.append("categoryId", String(categoryId));
  if (active !== undefined) fd.append("active", String(active));
  if (file) fd.append("media", file);
  return apiFetch(`/admin/gallery-photos/${id}`, { method: "PATCH", body: fd });
}

export async function deleteGalleryPhoto(id) {
  return apiFetch(`/admin/gallery-photos/${id}`, { method: "DELETE" });
}

// ─── Infrastructure categories ────────────────────────────────────────────────

export async function fetchInfrastructureCategories(isActive) {
  const q = isActive === undefined ? "" : `?isActive=${isActive}`;
  return apiFetch(`/admin/infrastructure-categories${q}`);
}

export async function createInfrastructureCategory(body) {
  return apiFetch("/admin/infrastructure-categories", { method: "POST", body });
}

export async function updateInfrastructureCategory(id, body) {
  return apiFetch(`/admin/infrastructure-categories/${id}`, { method: "PATCH", body });
}

export async function deleteInfrastructureCategory(id) {
  return apiFetch(`/admin/infrastructure-categories/${id}`, { method: "DELETE" });
}

// ─── Infrastructure items ─────────────────────────────────────────────────────

export async function fetchInfrastructureItems({ isActive, categoryId } = {}) {
  const params = new URLSearchParams();
  if (isActive !== undefined) params.set("isActive", String(isActive));
  if (categoryId !== undefined) params.set("categoryId", String(categoryId));
  const q = params.toString() ? `?${params}` : "";
  return apiFetch(`/admin/infrastructure-items${q}`);
}

export async function createInfrastructureItem({ title, description, categoryId, file }) {
  const fd = new FormData();
  fd.append("title", title);
  if (description) fd.append("description", description);
  fd.append("categoryId", String(categoryId));
  fd.append("media", file);
  return apiFetch("/admin/infrastructure-items", { method: "POST", body: fd });
}

export async function createInfrastructureItemsBulk({ categoryId, files, titlePrefix, description }) {
  const fd = new FormData();
  fd.append("categoryId", String(categoryId));
  if (titlePrefix) fd.append("titlePrefix", titlePrefix);
  if (description) fd.append("description", description);
  files.forEach((file) => fd.append("media", file));
  return apiFetch("/admin/infrastructure-items/bulk", { method: "POST", body: fd });
}

export async function updateInfrastructureItem(id, { title, description, categoryId, file, active }) {
  const fd = new FormData();
  if (title !== undefined) fd.append("title", title);
  if (description !== undefined) fd.append("description", description);
  if (categoryId !== undefined) fd.append("categoryId", String(categoryId));
  if (active !== undefined) fd.append("active", String(active));
  if (file) fd.append("media", file);
  return apiFetch(`/admin/infrastructure-items/${id}`, { method: "PATCH", body: fd });
}

export async function deleteInfrastructureItem(id) {
  return apiFetch(`/admin/infrastructure-items/${id}`, { method: "DELETE" });
}

// ─── Contact enquiries ───────────────────────────────────────────────────────

/** Public — footer Join Now, contact form, admission enquiry */
export async function submitContactEnquiry({ name, email, phone, classGrade, message, source }) {
  return apiFetch("/contact-enquiries", {
    method: "POST",
    body: { name, email, phone, classGrade, message, source },
  });
}

export async function fetchContactEnquiries() {
  return apiFetch("/admin/contact-enquiries");
}

export async function deleteContactEnquiry(id) {
  return apiFetch(`/admin/contact-enquiries/${id}`, { method: "DELETE" });
}

// ─── Custom alumni ───────────────────────────────────────────────────────────

export async function fetchPublicAlumni(isActive = true) {
  return apiFetch(`/alumni?isActive=${isActive}`);
}

export async function fetchAlumni(isActive) {
  const q = isActive === undefined ? "" : `?isActive=${isActive}`;
  return apiFetch(`/admin/alumni${q}`);
}

export async function createAlumni({ description }) {
  return apiFetch("/admin/alumni", { method: "POST", body: { description } });
}

export async function updateAlumni(id, { description, active }) {
  const body = {};
  if (description !== undefined) body.description = description;
  if (active !== undefined) body.active = active;
  return apiFetch(`/admin/alumni/${id}`, { method: "PATCH", body });
}

export async function deleteAlumni(id) {
  return apiFetch(`/admin/alumni/${id}`, { method: "DELETE" });
}
